import { useEffect } from 'react';

/**
 * Run a loader on mount, whenever its own inputs change, and whenever the shell
 * asks for a refresh.
 *
 * `refreshTick` is a counter the shell bumps to mean "reload now" — the value
 * never carries information, only the fact that it changed does. A dependency
 * array needs to see a value being used, so the read happens here, once, instead
 * of being explained away at every call site.
 *
 * Pass a `load` that is wrapped in `useCallback` with its real inputs as
 * dependencies. That is what ties a reload to an environment or range change, and
 * what keeps this effect from re-running on every render.
 *
 * The loader is handed an `isCancelled` probe. Check it before writing state: a
 * reload triggered while a previous one is still in flight would otherwise let the
 * slower response land last and overwrite the newer data.
 */
export function useLoader(
  load: (isCancelled: () => boolean) => void | Promise<void>,
  refreshTick: number,
): void {
  useEffect(() => {
    let cancelled = false;
    void refreshTick;
    void load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load, refreshTick]);
}
